// Business order from DT-421; ages are stored plural.
const LETTER_SIZE_ORDER = [
  "one-size",
  "os",
  "3xs",
  "xxxs",
  "2xs",
  "xxs",
  "xs",
  "s",
  "m",
  "l",
  "xl",
  "1x",
  "1xl",
  "2x",
  "2xl",
  "xxl",
  "3x",
  "3xl",
  "xxxl",
  "4x",
  "4xl",
  "xxxxl",
  "5x",
  "5xl",
  "xxxxxl",
  "6x",
  "6xl",
  "xxxxxxl",
  "7x",
  "7xl",
  "xxxxxxxl",
  "1-months",
  "3-months",
  "6-months",
  "12-months",
  "18-months",
  "2-years",
  "3-years",
  "5-years",
  "6-years",
  "7-years",
  "8-years",
  "10-years",
  "12-years",
  "14-years",
  "16-years",
  "18-years",
];

const LETTER_RANK = new Map(
  LETTER_SIZE_ORDER.map((size, index): [string, number] => [size, index])
);

const LETTER_HEAD_RE = /^(one[\s-]?size|[a-z0-9]+)(?:[\s-](month|year)s?)?/;
const ONE_SIZE_RE = /^one[\s-]?size$/;

// A fraction only follows a number after whitespace so "33/34" stays two numbers.
const TOKEN_RE =
  /\d+(?:[.,]\d+)?(?:\s+[1-9]\/[2-9]|\s*[½¼¾⅓⅔])?|[½¼¾⅓⅔]|[^\d½¼¾⅓⅔]+/g;
const NUMBER_TOKEN_RE =
  /^(\d*)(?:[.,](\d+))?\s*(?:([1-9])\/([2-9])|([½¼¾⅓⅔]))?$/;
const NUMERIC_START_RE = /^[\d½¼¾⅓⅔]/;
const VULGAR: Record<string, number> = {
  "½": 0.5,
  "¼": 0.25,
  "¾": 0.75,
  "⅓": 1 / 3,
  "⅔": 2 / 3,
};

type Token = { value: number } | { text: string };

type SortKey =
  | { tier: 0; rank: number; rest: string }
  | { tier: 1; tokens: Token[] }
  | { tier: 2 };

const collate = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });

const byCodePoint = (a: string, b: string): number => {
  if (a === b) return 0;
  return a < b ? -1 : 1;
};

const parseNumber = (token: string): number => {
  const match = NUMBER_TOKEN_RE.exec(token);
  if (!match) return 0;
  const [, whole, decimal, numerator, denominator, vulgar] = match;
  let value = Number(whole || 0);
  if (decimal) value += Number(`0.${decimal}`);
  if (numerator && denominator)
    value += Number(numerator) / Number(denominator);
  if (vulgar) value += VULGAR[vulgar];
  return value;
};

const tokenize = (label: string): Token[] =>
  (label.match(TOKEN_RE) ?? []).map((token) =>
    NUMERIC_START_RE.test(token)
      ? { value: parseNumber(token) }
      : { text: token }
  );

const letterKey = (
  label: string
): { rank: number; rest: string } | undefined => {
  const match = LETTER_HEAD_RE.exec(label);
  if (!match) return undefined;
  const [head, base, ageUnit] = match;
  const key = ageUnit
    ? `${base}-${ageUnit}s`
    : base.replace(ONE_SIZE_RE, "one-size");
  const rank = LETTER_RANK.get(key);
  if (rank === undefined) return undefined;
  return { rank, rest: label.slice(head.length) };
};

const sortKey = (label: string): SortKey => {
  const letter = letterKey(label);
  if (letter) return { tier: 0, ...letter };
  if (/\d/.test(label)) return { tier: 1, tokens: tokenize(label) };
  return { tier: 2 };
};

const compareTokens = (a: Token[], b: Token[]): number => {
  const length = Math.min(a.length, b.length);
  for (let i = 0; i < length; i += 1) {
    const x = a[i];
    const y = b[i];
    if ("value" in x && "value" in y) {
      if (x.value !== y.value) return x.value - y.value;
    } else if ("text" in x && "text" in y) {
      const diff = collate(x.text, y.text);
      if (diff !== 0) return diff;
    } else {
      return "value" in x ? -1 : 1;
    }
  }
  return a.length - b.length;
};

const compareKeys = (a: SortKey, b: SortKey): number => {
  if (a.tier !== b.tier) return a.tier - b.tier;
  if (a.tier === 0 && b.tier === 0) {
    return a.rank - b.rank || collate(a.rest, b.rest);
  }
  if (a.tier === 1 && b.tier === 1) return compareTokens(a.tokens, b.tokens);
  return 0;
};

export const compareSizeNames = (a: string, b: string): number => {
  const an = (a || "").toLowerCase().trim();
  const bn = (b || "").toLowerCase().trim();
  return (
    compareKeys(sortKey(an), sortKey(bn)) ||
    collate(an, bn) ||
    byCodePoint(an, bn)
  );
};
